import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixFunctionsV1FunctionFormSchema from './functions-v1-function-form-schema-function-form-schemas.http.js';

/** FunctionFormSchema entity */
export interface FunctionFormSchema {
  /**
   * FunctionFormSchema ID. Matches ID of the function for which it was created.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * The JSONSchema for FormTemplate, specifying input title, description, type, constraints.
   * @readonly
   */
  jsonSchema?: Record<string, any> | null;
  /**
   * The UI Schema for FormTemplate, containing the input display specification, currently only the placeholders.
   * @readonly
   */
  uiSchema?: Record<string, any> | null;
  /**
   * Field keys by input source.
   * @readonly
   * @maxSize 100
   */
  formFieldSources?: FormFieldSource[];
}

export enum FormFieldSourceType {
  SPI_CONFIG = 'SPI_CONFIG',
  ACTION_SET_VARIABLES = 'ACTION_SET_VARIABLES',
  ACTION_OUTPUT = 'ACTION_OUTPUT',
  ACTION_OUTPUT_1 = 'ACTION_OUTPUT_1',
  ACTION_OUTPUT_2 = 'ACTION_OUTPUT_2',
  ACTION_OUTPUT_3 = 'ACTION_OUTPUT_3',
  ACTION_OUTPUT_4 = 'ACTION_OUTPUT_4',
  ACTION_OUTPUT_5 = 'ACTION_OUTPUT_5',
  ACTION_OUTPUT_6 = 'ACTION_OUTPUT_6',
  ACTION_OUTPUT_7 = 'ACTION_OUTPUT_7',
  ACTION_OUTPUT_8 = 'ACTION_OUTPUT_8',
  ACTION_OUTPUT_9 = 'ACTION_OUTPUT_9',
  ACTION_CONDITION = 'ACTION_CONDITION',
  ACTION_APP_DEFINED_SET_VARIABLES = 'ACTION_APP_DEFINED_SET_VARIABLES',
}

/** @enumType */
export type FormFieldSourceTypeWithLiterals =
  | FormFieldSourceType
  | 'SPI_CONFIG'
  | 'ACTION_SET_VARIABLES'
  | 'ACTION_OUTPUT'
  | 'ACTION_OUTPUT_1'
  | 'ACTION_OUTPUT_2'
  | 'ACTION_OUTPUT_3'
  | 'ACTION_OUTPUT_4'
  | 'ACTION_OUTPUT_5'
  | 'ACTION_OUTPUT_6'
  | 'ACTION_OUTPUT_7'
  | 'ACTION_OUTPUT_8'
  | 'ACTION_OUTPUT_9'
  | 'ACTION_CONDITION'
  | 'ACTION_APP_DEFINED_SET_VARIABLES';

export interface FormFieldSource {
  /** The source for the input field. */
  formFieldSourceType?: FormFieldSourceTypeWithLiterals;
  /**
   * The field keys of the source. Limited by the target field constraints of the FormTemplate
   * @readonly
   * @maxSize 1000
   * @maxLength 200
   */
  formFieldKeys?: string[];
}

export interface GetFunctionFormSchemaRequest {
  /**
   * ID of the function of the returned FunctionFormSchema.
   * @format GUID
   */
  functionId: string;
}

export interface GetFunctionFormSchemaResponse {
  /** The requested FunctionFormSchema. */
  functionFormSchema?: FunctionFormSchema;
}

/**
 * Retrieves a FunctionFormSchema.
 * @param functionId - ID of the function of the returned FunctionFormSchema.
 * @internal
 * @documentationMaturity preview
 * @requiredField functionId
 * @permissionId FUNCTIONS.FUNCTION_FORM_SCHEMA_READ
 * @applicableIdentity APP
 * @returns The requested FunctionFormSchema.
 * @fqn wix.functions.formschemas.v1.FunctionFormSchemas.GetFunctionFormSchema
 */
export async function getFunctionFormSchema(
  functionId: string
): Promise<
  NonNullablePaths<
    FunctionFormSchema,
    `formFieldSources` | `formFieldSources.${number}.formFieldSourceType`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    functionId: functionId,
  });

  const reqOpts =
    ambassadorWixFunctionsV1FunctionFormSchema.getFunctionFormSchema(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.functionFormSchema!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { functionId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['functionId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
